import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bars3Icon,
  CheckIcon,
  ChevronLeftIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Redirect, Route, Link as RouterLink, Switch, useRouteMatch } from 'react-router-dom';

import usePage, { PageProvider } from '@dashboard/hooks/usePage';

import Button from '@dashboard/components/Button';
import { CheckoutShareButton } from '@dashboard/components/CheckoutShare';
import EditCheckoutNameDialog from '@dashboard/components/EditCheckoutNameDialog';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';

import CheckoutOverview from './scenes/CheckoutOverview';
import EventOverview from './scenes/EventOverview';
import FormOverview from './scenes/FormOverview';
import PageAnalytics from './scenes/PageAnalytics';
import PageCustomers from './scenes/PageCustomers';
import PagePayments from './scenes/PagePayments';
import PageSubmissions from './scenes/PageSubmissions';
import PageSubscriptions from './scenes/PageSubscriptions';
import PageBookings from './scenes/PageBookings';
import PageGuests from './scenes/PageGuests';
import { eventsAppUrl } from '@/dashboard/services/env';

function getPageTabs(page) {
  switch (page.type) {
    case 'checkout':
      return getCheckoutPageTabs(page.product?.type);
    case 'event':
      return eventPageTabs;
    case 'form':
      return formPageTabs;
    default:
      return [];
  }
}

const getCheckoutPageTabs = productType => [
  {
    path: 'overview',
    label: 'Overview',
    component: CheckoutOverview,
  },
  ...(productType === 'subscription'
    ? [
        {
          path: 'subscriptions',
          label: 'Subscriptions',
          component: PageSubscriptions,
        },
      ]
    : []),
  ...(productType === 'charge'
    ? [
        {
          path: 'payments',
          label: 'Payments',
          component: PagePayments,
        },
      ]
    : []),

  {
    path: 'customers',
    label: 'Customers',
    component: PageCustomers,
  },
  {
    path: 'analytics',
    label: 'Analytics',
    component: PageAnalytics,
  },
];

const eventPageTabs = [
  {
    path: 'overview',
    label: 'Overview',
    component: EventOverview,
  },
  {
    path: 'bookings',
    label: 'Bookings',
    component: PageBookings,
  },
  {
    path: 'guests',
    label: 'Guests',
    component: PageGuests,
  },
  {
    path: 'analytics',
    label: 'Analytics',
    component: PageAnalytics,
  },
];

const formPageTabs = [
  {
    path: 'overview',
    label: 'Overview',
    component: FormOverview,
  },
  {
    path: 'submissions',
    label: 'Submissions',
    component: PageSubmissions,
  },
  {
    path: 'leads',
    label: 'Leads',
    component: PageCustomers,
  },
  {
    path: 'analytics',
    label: 'Analytics',
    component: PageAnalytics,
  },
];

function PageOverview() {
  const { page, isLoading } = usePage();
  const match = useRouteMatch();
  const [navOpen, setNavOpen] = useState(false);
  const [editCheckoutNameDialogOpen, setEditCheckoutNameDialogOpen] = useState(false);

  const pageTabs = getPageTabs(page);

  const editButtonTitle =
    page.type === 'event' ? 'Edit event' : page.type === 'form' ? 'Edit form' : 'Edit checkout';

  return (
    <div>
      {!isLoading && (
        <div
          className={clsx(
            'hidden lg:hidden fixed z-30 mt-16 w-48 px-1.5 pt-4 bg-white h-screen border-r border-gray-100',
            {
              '!flex flex-col': navOpen,
            },
          )}
        >
          <div className="">
            {pageTabs.map(tab => (
              <Link
                key={tab.label}
                to={`/pages/${match.params.id}/customize/${tab.path}`}
                className={clsx(
                  `outline-none group flex items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
                  {
                    '!text-chocolate-600 bg-gray-50': location.pathname.includes(tab.path),
                    'text-gray-900 bg-transparent': !location.pathname.includes(tab.path),
                  },
                )}
                onClick={() => setNavOpen(false)}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <div className="mt-auto mb-20">
            <Link to={`/pages`}>
              <ChevronLeftIcon className="w-5 h-5 inline-block mr-2" />
              Pages
            </Link>
          </div>
        </div>
      )}

      <div className="relative">
        <nav
          className="w-full flex h-16 border-b border-gray-100 px-4 items-center fixed z-40 bg-white"
          style={{ maxWidth: '2560px' }}
        >
          {!isLoading && (
            <>
              <div className="absolute left-4">
                <div className="block lg:hidden z-50">
                  <Button appearance="light" onClick={() => setNavOpen(!navOpen)}>
                    <Bars3Icon className="w-5 h-5" />

                    <div className="sr-only">Toggle Nav</div>
                  </Button>
                </div>

                <div className="hidden lg:flex items-center">
                  <RouterLink
                    to={`/pages`}
                    className="text-black font-medium hover:text-chocolate-600 inline-flex items-center"
                    bare
                  >
                    <ArrowLeftIcon className="w-5 h-5 inline-block mr-1.5" />
                    Pages
                  </RouterLink>

                  <span className=" text-gray-300 mx-2 text-xl hidden xl:block">{' / '}</span>

                  <span className="text-black font-medium items-center gap-x-1.5 max-w-[18rem] hidden xl:inline-flex">
                    <span className="truncate">{page.name}</span>

                    <Button
                      appearance="icon-button"
                      onClick={() => setEditCheckoutNameDialogOpen(true)}
                    >
                      <PencilIcon className="w-5 h-5 inline-block" />
                    </Button>

                    <EditCheckoutNameDialog
                      isOpen={editCheckoutNameDialogOpen}
                      onClose={() => setEditCheckoutNameDialogOpen(false)}
                      checkout={page}
                    />
                  </span>
                </div>
              </div>

              <div className="w-full text-center gap-x-2 items-center mx-auto justify-center bg-white z-10 max-w-lg hidden lg:flex">
                {pageTabs.map(tab => (
                  <Link
                    key={tab.label}
                    to={`/pages/${match.params.id}/${tab.path}`}
                    className={clsx(
                      `outline-none group flex items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
                      {
                        '!text-chocolate-600 bg-gray-50': location.pathname.includes(tab.path),
                        'text-gray-900 bg-transparent': !location.pathname.includes(tab.path),
                      },
                    )}
                  >
                    {tab.label}
                  </Link>
                ))}
              </div>

              <div className="absolute right-4 space-x-3">
                {page.type === 'event' ? (
                  <Button
                    appearance="secondary"
                    target="_blank"
                    href={`${eventsAppUrl}/event/${page._id}/guest-list`}
                  >
                    Check in guests
                    <CheckIcon className="w-4 h-4 ml-2 align-baseline" />
                  </Button>
                ) : (
                  <CheckoutShareButton appearance="secondary" checkoutId={match.params.id}>
                    Share & embed
                  </CheckoutShareButton>
                )}

                <Button appearance="primary" href={`/pages/${match.params.id}/customize`}>
                  {editButtonTitle}
                  <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
                </Button>
              </div>
            </>
          )}
        </nav>
      </div>

      <div>
        <div className="h-full pt-16">
          {isLoading ? (
            <div className="w-full h-[calc(100vh-128px)] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="bg-white px-4 h-full min-h-screen pt-4">
              <Switch>
                <Redirect
                  exact
                  from={`/pages/${match.params.id}`}
                  to={`/pages/${match.params.id}/overview`}
                />

                {pageTabs.map(tab => (
                  <Route path={`/pages/${match.params.id}/${tab.path}`} key={tab.path}>
                    <tab.component page={page} />
                  </Route>
                ))}
              </Switch>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PageOverviewContainer = () => {
  const match = useRouteMatch();

  return (
    <PageProvider pageId={match.params.id}>
      <PageOverview />
    </PageProvider>
  );
};

export default PageOverviewContainer;
